// src/lib/format.js — utilitaires de formatage chiffres/dates et constantes mois.
// Pures, pas de dépendance externe. Importables côté App + tabs + helpers.

export const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"]
export const MONTHS_SHORT = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"]

// ── Le zéro qui ment (paquet argent 2026-09-03) ─────────────────────
// NaN est falsy : l'ancien `Number(n||0)` maquillait tout calcul raté
// (NaN/Infinity) en « 0.00 » — un montant faux affiché comme un vrai zéro.
// Règle : un montant NON CALCULABLE s'affiche « — », JAMAIS « 0,00 € ».
// null / undefined / '' restent « 0.00 » (absence volontaire, gérée par
// l'appelant) ; un VRAI 0 reste « 0.00 » (0 € est une valeur légitime).
export const MONTANT_INCALCULABLE = '—'
export const TITRE_MONTANT_INCALCULABLE = 'montant incalculable — donnée manquante'
// true ssi la valeur est fournie mais non convertible en nombre fini.
export const montantIncalculable = (n) => n != null && n !== '' && !Number.isFinite(Number(n))
export const fmt2 = n => montantIncalculable(n) ? MONTANT_INCALCULABLE : Number(n||0).toFixed(2)
export const fmtE = n => montantIncalculable(n) ? MONTANT_INCALCULABLE : `${fmt2(n)} €`
export const daysInMonth = (y, m) => new Date(y, m, 0).getDate()
export const uid = () => Math.random().toString(36).slice(2, 9)
export const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—'
export const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }

// "2026-04" → "Avril 2026". Retourne la chaîne brute si format non reconnu.
export const formatMois = (m) => {
  if (!/^\d{4}-\d{2}$/.test(m || "")) return m || "?"
  const [y, mm] = m.split("-")
  return `${MONTHS[parseInt(mm) - 1]} ${y}`
}

// ISO date → "JJ/MM" (court, sans année).
export const fmtJourMois = (iso) => {
  if (!iso) return "?"
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`
}

// ISO date / Date → "ven 1 mai 2026" (utilisé Phase 2.G.1 dans ResaV2Modal)
export const formatDateFR = (d) => {
  if (!d) return "—"
  try {
    const dt = d instanceof Date ? d : new Date(d)
    if (isNaN(dt.getTime())) return "—"
    return new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(dt)
  } catch (_) { return "—" }
}

// ISO timestamp → "ven 4 mai 2026 à 14:32" (utilisé Phase 2.G.2 horodatage)
export const formatDateTimeFR = (ts) => {
  if (!ts) return "—"
  try {
    const dt = ts instanceof Date ? ts : new Date(ts)
    if (isNaN(dt.getTime())) return "—"
    const date = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(dt)
    const time = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(dt)
    return `${date} à ${time}`
  } catch (_) { return "—" }
}

// ── Affichage proprio (Phase ADR-050) ────────────────────────────────
// Reconstruit le libellé d'affichage standard FBZ : "NOM Prénom".
//
// Format métier validé par Dimitri : nom de famille en MAJUSCULES,
// prénom en casse normale, séparés par 1 espace. Saisie côté admin en
// casse libre, .toUpperCase() appliqué uniquement à l'affichage.
//
// Source de vérité : cfg.props[].nom_famille + cfg.props[].prenom.
// Le champ legacy cfg.props[].nom (string complet "AVELAR Bruno") est
// conservé comme CHAMP DÉRIVÉ recalculé à chaque saveCfg via cette
// fonction — permet aux ~30 call-sites historiques qui lisent
// `prop.nom` de continuer à marcher sans refacto massif.
//
// Fallback (compat ascendant) : si ni `nom_famille` ni `prenom` ne sont
// présents (= proprios issus du modèle pré-ADR-050, non encore migrés
// par scripts/migrate-proprios-split-nom.js), on retourne `prop.nom` tel
// quel. Garantit que la fonction est safe à utiliser dès maintenant
// même avant la migration des données.
//
// @param {Object} prop - cfg.props[i]
// @returns {string} libellé affiché. Vide si prop falsy.
export const propDisplayName = (prop) => {
  if (!prop) return ''
  const nf = String(prop.nom_famille || '').trim().toUpperCase()
  const pn = String(prop.prenom || '').trim()
  if (nf || pn) return [nf, pn].filter(Boolean).join(' ')
  return prop.nom || ''
}

// Nombre de nuits entre 2 dates ISO/Date (différence start_date → end_date)
export const nightsBetween = (start, end) => {
  if (!start || !end) return 0
  try {
    const s = start instanceof Date ? start : new Date(start)
    const e = end instanceof Date ? end : new Date(end)
    const diff = e - s
    if (isNaN(diff) || diff <= 0) return 0
    return Math.round(diff / (1000 * 60 * 60 * 24))
  } catch (_) { return 0 }
}
